import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { updateProfile, clearAuthError } from '../../actions/userActions';
import { toast } from 'react-toastify';
import { clearUpdateProfile } from '../../slices/authSlice';
import { User, Mail, Upload, Save, Phone, MapPin } from 'lucide-react';

export default function UpdateProfile() {
    const dispatch = useDispatch();
    const { error, user, isUpdated } = useSelector(state => state.authState);

    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [address, setAddress] = useState('');
    const [avatar, setAvatar] = useState(null);
    const [avatarPreview, setAvatarPreview] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (user) {
            setName(user.name || '');
            setEmail(user.email || '');
            setPhone(user.phone || '');
            setAddress(user.address || '');
            setAvatarPreview(user.avatar);
        }
    }, [user]);

    const handleAvatarChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setAvatar(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setAvatarPreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handlePhoneChange = (e) => {
        // Only allow numbers, limit to 10 digits
        const value = e.target.value.replace(/\D/g, '');
        if (value.length <= 10) {
            setPhone(value);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Validate phone number
        if (phone.length !== 10) {
            toast.error('Please enter a valid 10-digit phone number');
            return;
        }
        
        // Validate address
        if (!address.trim()) {
            toast.error('Please provide your address');
            return;
        }
        
        setIsSubmitting(true);
        
        const formData = new FormData();
        formData.append('name', name);
        formData.append('email', email);
        formData.append('phone', phone);
        formData.append('address', address);
        
        if (avatar) {
            formData.append('avatar', avatar);
        }
        
        await dispatch(updateProfile(formData));
        setIsSubmitting(false);
    };

    useEffect(() => {
        if (isUpdated) {
            toast.success('Profile updated Successfully', {
                onOpen: () => dispatch(clearUpdateProfile())
            });
        }

        if (error) {
            toast.error(error, {
                onOpen: () => dispatch(clearAuthError())
            });
        }
    }, [isUpdated, error, dispatch]);

    return (
        <div className="max-w-2xl mx-auto px-4 py-8">
            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                <div className="p-6">
                    <h2 className="text-2xl font-bold text-[#dc2626] mb-6 font-['Crimson_Text'] text-center">
                        Update Profile
                    </h2>

                    <form onSubmit={handleSubmit} encType="multipart/form-data" className="space-y-6">
                        {/* Name Input */}
                        <div>
                            <label htmlFor="name_field" className="block text-sm font-medium text-gray-700 mb-1">
                                Full Name
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <User className="h-5 w-5 text-gray-400" />
                                </div>
                                <input
                                    type="text"
                                    id="name_field"
                                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-[#dc2626] focus:border-[#dc2626] text-center"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    placeholder="Enter your full name"
                                    required
                                />
                            </div>
                        </div>

                        {/* Email Input */}
                        <div>
                            <label htmlFor="email_field" className="block text-sm font-medium text-gray-700 mb-1">
                                Email Address
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <Mail className="h-5 w-5 text-gray-400" />
                                </div>
                                <input
                                    type="email"
                                    id="email_field"
                                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-[#dc2626] focus:border-[#dc2626] text-center"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="Enter your email"
                                    required
                                />
                            </div>
                        </div>

                        {/* Phone Input */}
                        <div>
                            <label htmlFor="phone_field" className="block text-sm font-medium text-gray-700 mb-1">
                                Phone Number
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <Phone className="h-5 w-5 text-gray-400" />
                                </div>
                                <input
                                    type="tel"
                                    id="phone_field"
                                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-[#dc2626] focus:border-[#dc2626] text-center"
                                    value={phone}
                                    onChange={handlePhoneChange}
                                    placeholder="Enter 10-digit phone number"
                                    maxLength="10"
                                    pattern="[0-9]{10}"
                                    required
                                />
                            </div>
                            {phone && phone.length !== 10 && (
                                <p className="mt-1 text-sm text-red-600">Please enter a valid 10-digit phone number</p>
                            )}
                        </div>

                        {/* Address Input */}
                        <div>
                            <label htmlFor="address_field" className="block text-sm font-medium text-gray-700 mb-1">
                                Address
                            </label>
                            <div className="relative">
                                <div className="absolute top-3 left-3 flex items-center pointer-events-none">
                                    <MapPin className="h-5 w-5 text-gray-400" />
                                </div>
                                <textarea
                                    id="address_field"
                                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-[#dc2626] focus:border-[#dc2626] text-center min-h-[100px] resize-none"
                                    value={address}
                                    onChange={(e) => setAddress(e.target.value)}
                                    placeholder="Enter your full address"
                                    required
                                />
                            </div>
                        </div>

                        {/* Avatar Upload */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Profile Picture
                            </label>
                            <div className="flex items-center space-x-6">
                                <div className="w-20 h-20 rounded-full overflow-hidden border-2 border-gray-200">
                                    <img
                                        src={avatarPreview || user.avatar}
                                        alt="Avatar Preview"
                                        className="w-full h-full object-cover"
                                    />
                                </div>
                                <div className="flex-1">
                                    <label
                                        htmlFor="avatar_upload"
                                        className="flex items-center justify-center px-4 py-2 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50"
                                    >
                                        <Upload className="h-5 w-5 text-gray-400 mr-2" />
                                        <span className="text-sm text-gray-600">Choose new picture</span>
                                        <input
                                            type="file"
                                            id="avatar_upload"
                                            className="hidden"
                                            accept="image/*"
                                            onChange={handleAvatarChange}
                                        />
                                    </label>
                                </div>
                            </div>
                        </div>

                        {/* Submit Button */}
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className="w-full flex items-center justify-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-[#dc2626] hover:bg-[#dc2626]/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#dc2626] disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <Save className="h-5 w-5 mr-2" />
                            {isSubmitting ? 'Updating...' : 'Update Profile'}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
}